// Parses a small HTML subset into a node tree. No HTML string is ever built from the input:
// text becomes React children, and only <a href/target> and a <span> with a class in SPAN_CLASSES keep an attribute.
const ALLOWED = ["b", "strong", "i", "em", "u", "br", "p", "ul", "ol", "li", "a", "span"];

const VOID = ["br"];
// Dropped together with their content when unknown tags are dropped, since their content is code, not text.
const CODE_TAGS = ["script", "style"];
// When dropped, the start and end of one of these become a line break so its text stays apart from the text around it.
const BLOCK_TAGS = ["hr", "div", "section", "article", "header", "footer", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre", "table", "tr", "dl", "dt", "dd", "figure", "figcaption"];
// When dropped, the end of one of these becomes a space so table cells stay apart.
const CELL_TAGS = ["td", "th"];
const COMMENT = /<!--[\s\S]*?(-->|$)/g;
const TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^<>]*?)(\/?)>/g;
const SAFE_HREF = /^(https?:|mailto:|\/(?![/\\]))/i;
const SPAN_CLASSES = { "rasputin-muted": "muted", "rasputin-code": "code" };

const ENTITIES = {
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    "#39": "'",
    nbsp: " ",
};

function decode(text) {
    return text.replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (_, name) => ENTITIES[name]);
}

function attributeValue(attributes, name) {
    const match = new RegExp(`${name}\\s*=\\s*(?:(["'])(.*?)\\1|(\\S+))`, "is").exec(attributes);
    return match?.[2] ?? match?.[3];
}

function linkAttributes(attributes) {
    const href = decode(attributeValue(attributes, "href") ?? "").trim();
    const target = attributeValue(attributes, "target");

    return {
        href: SAFE_HREF.test(href) ? href : undefined,
        newTab: target === "_blank" || target === "blank",
    };
}

function spanAttributes(attributes) {
    const className = attributeValue(attributes, "class");

    return { variant: Object.hasOwn(SPAN_CLASSES, className) ? SPAN_CLASSES[className] : undefined };
}

function codeTagEnd(input, tag, from) {
    const end = new RegExp(`</${tag}\\s*>`, "i").exec(input.slice(from));
    return end ? from + end.index + end[0].length : input.length;
}

// Tags outside the allowlist stay visible as text, or with dropUnknownTags are removed while their text is kept.
export function parseMarkup(input, { dropUnknownTags = false } = {}) {
    const root = [];
    const stack = [];
    const children = () => stack.at(-1)?.children ?? root;

    function pushText(text) {
        if (!text) return;

        const target = children();
        const last = target.at(-1);
        if (last?.kind === "text") {
            last.text += decode(text);
        } else {
            target.push({ kind: "text", text: decode(text) });
        }
    }

    function pushBreak() {
        const target = children();
        const last = target.at(-1);
        if (last && last.tag !== "br") {
            target.push({ kind: "element", tag: "br", children: [] });
        }
    }

    if (dropUnknownTags) input = input.replace(COMMENT, "");

    let cursor = 0;
    for (const match of input.matchAll(TAG)) {
        const [raw, closing, name, attributes, selfClosing] = match;
        if (match.index < cursor) continue;

        const tag = name.toLowerCase();
        pushText(input.slice(cursor, match.index));
        cursor = match.index + raw.length;

        if (!ALLOWED.includes(tag)) {
            if (!dropUnknownTags) {
                pushText(raw);
            } else if (CODE_TAGS.includes(tag) && !closing && !selfClosing) {
                cursor = codeTagEnd(input, tag, cursor);
            } else if (BLOCK_TAGS.includes(tag)) {
                pushBreak();
            } else if (CELL_TAGS.includes(tag) && closing) {
                pushText(" ");
            }
            continue;
        }

        if (closing) {
            const open = stack.map((entry) => entry.tag).lastIndexOf(tag);
            if (open === -1) {
                if (!dropUnknownTags) pushText(raw);
            } else {
                stack.length = open;
            }
            continue;
        }

        const element = {
            kind: "element",
            tag,
            ...(tag === "a" ? linkAttributes(attributes) : {}),
            ...(tag === "span" ? spanAttributes(attributes) : {}),
            children: [],
        };
        children().push(element);

        if (!selfClosing && !VOID.includes(tag)) {
            stack.push({ tag, children: element.children });
        }
    }
    pushText(input.slice(cursor));

    return root;
}
